import { IChat } from '@v2/domains/chat/types/chat.types'
import {
  IBasicEntity,
  IUser,
  PersonModel,
} from '@v2/domains/person/types/person.types'

import { type OrderByDatesEnum } from 'src/v2/commons/interface/enums'

import {
  EscalationIssueEnum,
  EscalationProgramEnum,
  EscalationStatusEnum,
} from '../escalation/escalation.types'

export enum TaskActionEnum {
  OTHER = 'other',
  RATE_DRIVER = 'rateDriver',
  MAKE_CALL = 'makeCall',
  OFFICE_VISIT = 'Office Visit',
}

export enum TaskSubjectEnum {
  CALL_DRIVER = 'callDriver',
  URGENT_CALL = 'urgentCall',
  LEAVE_VOICE_MAIL = 'leaveVoiceMail',
  ASSISTANCE = 'assistance',
  RATE_DRIVER = 'rateDriver',
  ENROLL_DRIVER = 'enrollDriver',
  DRIVER_ASSITANCE = 'driversAssistance',
  ISSUE_WITH_BENEFITS = 'issueWithBenefits',
  APP_ISSUE = 'appIssue',
}

export enum TaskTypeEnum {
  FOLLOW_UP = 'FOLLOW_UP',
  ESCALATION = 'ESCALATION',
  OFFICE_VISIT = 'OFFICE_VISIT',
}

export enum TaskPriorityEnum {
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
}

export enum TaskStatusEnum {
  OPEN = 'open',
  IN_PROGRESS = 'inProgress',
  COMPLETED = 'completed',
}

export interface ITask extends IBasicEntity {
  id: string
  type: TaskTypeEnum
  action: TaskActionEnum
  subject: string
  assignedToId: string
  assignedTo: IUser
  person: PersonModel
  activityDate?: string
  priority: TaskPriorityEnum
  isPersonAssign: boolean
  note: string
  isNotify: boolean
  personId: string
  createdBy: IUser
  updatedBy: IUser
  chat?: IChat
  status?: TaskStatusEnum
  completedDate?: null | string
  officeVisit?: {
    id: string
  }
  escalation?: IEscalationTask
}

export interface IEscalationTask {
  id: string
  program: EscalationProgramEnum
  formStatus: EscalationStatusEnum
  issue: EscalationIssueEnum
}
export interface ITaskBody extends Partial<Omit<MutateTaskData, 'personId'>> {
  personIds: string[]
  excludedPersonIds?: string[]
  isAllPersonSelected?: boolean
  organizationId: string
}

export interface MutateTasksData {
  body: ITaskBody
}

export type MutateTaskData = Partial<
  Omit<ITask, 'activityDate' | 'updatedAt' | 'createdAt'>
> & {
  activityDate?: Date | null
  updatedAt?: Date | null
  createdAt?: Date | null
}
export interface IGetTasksParams {
  organizationId?: string
  limit?: number
  offset?: number
  assignedToUserId?: string | null
  search?: string
  type?: TaskTypeEnum
  action?: TaskActionEnum
  excludeAction?: boolean
  fromDate?: Date
  toDate?: Date
  completeFromDate?: Date
  completeToDate?: Date
  priority?: TaskPriorityEnum
  subject?: string
  status?: TaskStatusEnum
  orderBy?: OrderByDatesEnum
}

export const TASK_STATUS_OPTIONS = [
  { value: TaskStatusEnum.OPEN, label: 'Open' },
  { value: TaskStatusEnum.IN_PROGRESS, label: 'In Progress' },
  { value: TaskStatusEnum.COMPLETED, label: 'Completed' },
]

export const TASK_ACTION_OPTIONS = [
  {
    label: 'Make a Call',
    value: TaskActionEnum.MAKE_CALL,
  },
  {
    label: 'Rate Driver',
    value: TaskActionEnum.RATE_DRIVER,
  },
  {
    label: 'Other',
    value: TaskActionEnum.OTHER,
  },
]

export const TASK_TYPE_OPTIONS = [
  { label: 'Follow-Up', value: TaskTypeEnum.FOLLOW_UP },
  { label: 'Escalation', value: TaskTypeEnum.ESCALATION },
]

export const TASK_PRIORITY_OPTIONS = [
  { label: 'High', value: TaskPriorityEnum.HIGH },
  { label: 'Medium', value: TaskPriorityEnum.MEDIUM },
  { label: 'Low', value: TaskPriorityEnum.LOW },
]

export const TASK_SUBJECT_OPTIONS = [
  {
    value: TaskSubjectEnum.CALL_DRIVER,
    label: 'Call Driver',
    action: [TaskActionEnum.MAKE_CALL],
    disabled: false,
  },
  {
    value: TaskSubjectEnum.URGENT_CALL,
    label: 'Urgent Call',
    action: [TaskActionEnum.MAKE_CALL],
    disabled: false,
  },
  {
    value: TaskSubjectEnum.LEAVE_VOICE_MAIL,
    label: 'Leave Voice Mail',
    action: [TaskActionEnum.MAKE_CALL],
    disabled: false,
  },
  {
    value: TaskSubjectEnum.ASSISTANCE,
    label: 'Assistance',
    action: [TaskActionEnum.MAKE_CALL],
    disabled: false,
  },
  {
    value: TaskSubjectEnum.RATE_DRIVER,
    label: 'Please Rate the Driver',
    action: [TaskActionEnum.RATE_DRIVER],
    disabled: false,
  },
  {
    value: TaskSubjectEnum.ENROLL_DRIVER,
    label: 'Enroll Driver',
    action: [TaskActionEnum.OTHER],
    disabled: false,
  },
  {
    value: TaskSubjectEnum.DRIVER_ASSITANCE,
    label: 'Drivers needs assistance',
    action: [TaskActionEnum.OTHER],
    disabled: false,
  },
  {
    value: TaskSubjectEnum.ISSUE_WITH_BENEFITS,
    label: 'Issue with benefits',
    action: [TaskActionEnum.OTHER],
    disabled: false,
  },
  {
    value: TaskSubjectEnum.APP_ISSUE,
    label: 'App Issue',
    action: [TaskActionEnum.OTHER],
    disabled: false,
  },
]

export const ESCALATION_ISSUE_OPTIONS = [
  {
    label: EscalationIssueEnum.UTILIZATION,
    value: EscalationIssueEnum.UTILIZATION,
  },
  {
    label: EscalationIssueEnum.HEALTH,
    value: EscalationIssueEnum.HEALTH,
  },
  {
    label: EscalationIssueEnum.ENROLLMENT,
    value: EscalationIssueEnum.ENROLLMENT,
  },
  {
    label: EscalationIssueEnum.OTHER,
    value: EscalationIssueEnum.OTHER,
  },
  {
    label: EscalationIssueEnum.COMPENSATION,
    value: EscalationIssueEnum.COMPENSATION,
  },
  {
    label: EscalationIssueEnum.EDUCATION,
    value: EscalationIssueEnum.EDUCATION,
  },
  {
    label: EscalationIssueEnum.CLASSES,
    value: EscalationIssueEnum.CLASSES,
  },
  {
    label: EscalationIssueEnum.APP,
    value: EscalationIssueEnum.APP,
  },
  {
    label: EscalationIssueEnum.MEMBERSHIP,
    value: EscalationIssueEnum.MEMBERSHIP,
  },
  {
    label: EscalationIssueEnum.ORGANAZING,
    value: EscalationIssueEnum.ORGANAZING,
  },
]
